import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Identify Ship and Debit Customer Step
 *
 * Identifies which customer a Ship and Debit email is from by matching the email's
 * "to" address against configured customer mappings.
 *
 * This step looks up the customer based on email patterns and returns
 * the customer information along with which processor type to use for
 * detecting acceptance/rejection status.
 */
export const identifySDCustomerStep = defineStepConfig({
  type: "identify_sd_customer",
  name: "Identify SD Customer",
  description:
    "Identify which customer a Ship and Debit email is from based on email address",
  category: "Transformation",
  libraryModule: "ShipAndDebit",
  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The inbound email to identify the customer from",
    ),
  }),

  outputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "Pass-through of the inbound email for downstream steps",
    ),
    customerFound: z
      .boolean()
      .describe("Whether a customer mapping was found for this email"),
    customerId: z
      .string()
      .nullable()
      .describe("Internal customer identifier, null if not found"),
    customerName: z
      .string()
      .nullable()
      .describe("Human-readable customer name, null if not found"),
    processorType: z
      .string()
      .nullable()
      .describe(
        "Which SD processor to use for this customer, null if not found",
      ),
    customerConfig: z
      .record(z.string(), z.unknown())
      .nullable()
      .describe("Customer-specific configuration for the processor"),
  }),

  configSchema: z.object({
    customerMappings: z
      .record(
        z.string(),
        z.object({
          senderPatterns: z.array(z.string()).optional(),
          processorType: z.enum(["config", "generic", "spsx"]).optional(),
        }),
      )
      .optional()
      .describe(
        "Map of customer name → { senderPatterns, processorType }. The sender email is matched against each customer's patterns to identify who sent the email.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 seconds",
    retry: {
      maximumAttempts: 2,
    },
  },
});
